<template>
  <v-container>
    <h2>Season Management</h2>
    <br>
    <v-btn color="primary" dark class="mb-3" block @click="createNewSeason">New Season</v-btn>
    <v-text-field class="ma-auto" v-model="search" append-icon="mdi-magnify" label="Search" single-line hide-details/>
    <br>
    <v-data-table :headers="headers" :items="seasons" :search="search" sort-by="calories" class="elevation-1">
      <template v-slot:item.startTimeUtc="{ item }">
        <div>{{ item.startTimeUtc | formatDate}}</div>
      </template>
      <template v-slot:item.endTimeUtc="{ item }">
        <div>{{ item.endTimeUtc | formatDate}}</div>
      </template>
      <template v-slot:item.requiresExplicitChallengeLink="{ item }">
        <v-simple-checkbox v-model="item.requiresExplicitChallengeLink" disabled align="center"></v-simple-checkbox>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-icon small class="mr-2" @click="editItem(item)">mdi-pencil</v-icon>
        <v-icon small @click="deleteItem(item, false)">mdi-delete</v-icon>
      </template>
    </v-data-table>
    <v-card-text>* all dates and times in local time zone<br>
                 ** seasons with required challenge links will ONLY receive results if the actives changes refer to it with the 'seasonId' field</v-card-text>
    <v-dialog v-model="showNewEditSeasonDialog" persistent max-width="680">
      <v-card>
        <v-card-title class="text-h5">Season Details</v-card-title>
        <v-text-field class="mx-5" v-model="editedItem.name" solo autocomplete label="Name" :rules="[v => v.length >= 3 || 'Min 3 characters']"/>
        <v-textarea class="mx-5" v-model="editedItem.description" solo autocomplete label="Description"/>
        <v-row justify="space-around">
          <div>
            <div>Start date UTC</div>
            <v-date-picker v-model="editedItem.startTimeUtc" color="green lighten-1"></v-date-picker>
          </div>
          <div>
            <div>End date UTC</div>
            <v-date-picker v-model="editedItem.endTimeUtc"></v-date-picker>
          </div>
        </v-row>
        <v-row justify="end">
          <v-switch class="mr-8" inset v-model="editedItem.requiresExplicitChallengeLink" label="Requires explicit challenge link" />
        </v-row>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="green darken-1" text @click="showNewEditSeasonDialog = false">Cancel</v-btn>
          <v-btn color="green darken-1" text @click="saveSeason()">Save</v-btn>
        </v-card-actions>
      </v-card>
      <v-alert v-model="showDlgErrorMessage" type="error" dismissible>{{ errorDlgMessage }}</v-alert>
    </v-dialog>
    <v-dialog v-model="showForceDeleteDialog" persistent max-width="680">
      <v-card>
        <v-card-title class="text-h5">Force Delete Season</v-card-title>
        <v-card-text>{{ errorMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="green darken-1" text @click="showForceDeleteDialog = false">Cancel</v-btn>
          <v-btn color="green darken-1" text @click="deleteItem(editedItem, true)">Force Delete</v-btn>
        </v-card-actions>
      </v-card>
      <v-alert v-model="showDlgErrorMessage" type="error" dismissible>{{ errorDlgMessage }}</v-alert>
    </v-dialog>
    <v-alert v-model="showErrorMessage" type="error" dismissible>{{ errorMessage }}</v-alert>
  </v-container>
</template>

<script>

import {eSailServerAuth} from "@/plugins/axios";
import _ from "lodash";
import {trim} from "lodash/string";
import moment from "moment";

export default {
  name: "Seasons",
  data: () => ({
    headers: [
      { text: 'Id', value: 'id' },
      { text: 'Name', value: 'name' },
      { text: 'Description', value: 'description' },
      { text: 'Start*', value: 'startTimeUtc' },
      { text: 'End*', value: 'endTimeUtc' },
      { text: 'Link Required**', value: 'requiresExplicitChallengeLink' },
      { text: 'Actions', value: 'actions', sortable: false },
    ],
    search: '',
    seasons: [],
    showDeleteSeasonDialog: false,
    showNewEditSeasonDialog: false,
    showForceDeleteDialog: false,
    showErrorMessage: false,
    showDlgErrorMessage: false,
    errorMessage: '',
    errorDlgMessage: '',
    editedItem: {
      id: 0,
      name: '',
      description: '',
      startTimeUtc: null,
      endTimeUtc: null,
      requiresExplicitChallengeLink: false
    }
  }),
  mounted() {
    this.getSeasons()
  },
  methods: {
    getSeasons() {
      eSailServerAuth.get('admin/seasons', { headers: { Authorization: `Bearer ` + this.$auth.token() }})
        .then(response => {
          this.seasons = response.data
        })
    },
    createNewSeason() {
      this.editedItem.id = 0
      this.editedItem.name = ''
      this.editedItem.description = ''
      this.editedItem.startTimeUtc = null
      this.editedItem.endTimeUtc = null
      this.editedItem.requiresExplicitChallengeLink = false
      this.showNewEditSeasonDialog = true
    },
    editItem (item) {
      item.startTimeUtc = moment(item.startTimeUtc).format('YYYY-MM-DD')
      item.endTimeUtc = moment(item.endTimeUtc).format('YYYY-MM-DD')
      this.editedItem = item
      this.showNewEditSeasonDialog = true
    },
    deleteItem (item, forceDelete) {
      eSailServerAuth.delete('admin/seasons/' + item.id + "?force-delete=" + forceDelete,{ headers: { Authorization: `Bearer ` + this.$auth.token() }})
        .then(() => {
          this.showNewEditSeasonDialog = false
          this.showForceDeleteDialog = false
          this.showErrorMessage = false
          this.getSeasons()
        })
        .catch((err) => {
          let response = err.response
          console.log(response.data.message)
          this.errorMessage = response.data.message
          if (response.status === 403) {
            this.editedItem.id = item.id
            this.showForceDeleteDialog = true
          } else {
            this.showErrorMessage = true
          }
        })
    },
    saveSeason() {
      if (trim(this.editedItem.name).length < 3) {
        this.errorDlgMessage = "Season name must contain at least 3 characters!"
        this.showDlgErrorMessage = true
        return
      }
      if (this.editedItem.startTimeUtc === undefined || this.editedItem.endTimeUtc === undefined) {
        this.errorDlgMessage = "Either end date or start date is not specified!"
        this.showDlgErrorMessage = true
        return
      }
      if (this.editedItem.endTimeUtc < this.editedItem.startTimeUtc) {
        this.errorDlgMessage = "End date is before start date!"
        this.showDlgErrorMessage = true
        return
      }
      let payload = _.clone(this.editedItem)
      payload.startTimeUtc += "T00:00:00.000+00:00"
      payload.endTimeUtc += "T23:59:59.000+00:00"
      let promise = (payload.id !== null && payload.id > 0)
          ? eSailServerAuth.put('admin/seasons/' + payload.id, payload, {headers: {Authorization: `Bearer ` + this.$auth.token()}})
          : eSailServerAuth.post('admin/seasons', payload, {headers: {Authorization: `Bearer ` + this.$auth.token()}})
      promise
          .then(() => {
            this.showNewEditSeasonDialog = false
            this.getSeasons()
          })
          .catch((err) => {
            this.errorDlgMessage = err.response.data.message
            this.showDlgErrorMessage = true
          })
    }
  }
}

</script>

<style scoped>

</style>